import React, { Component, createRef } from 'react';
import Moment from 'react-moment';
import { Link } from 'react-router-dom';
import MessageDetailItem from './MessageDetailItem';

// Số lượng tin nhắn tối đa được render cùng lúc
const MAX_VISIBLE_MESSAGES = 100;
// Số lượng tin nhắn được load thêm mỗi lần scroll lên
const LOAD_MORE_BATCH = 50;
// Khoảng cách từ đầu để trigger load more (pixels)
const LOAD_MORE_THRESHOLD = 200;

export default class MessageDetailList extends Component {
    constructor(props) {
        super(props);
        this.state = {
            visibleStartIndex: 0,
            visibleEndIndex: MAX_VISIBLE_MESSAGES,
            isLoadingMore: false
        };
        this.scrollContainerRef = createRef();
        this.sentinelRef = createRef();
        this.handleScroll = this.handleScroll.bind(this);
    }

    componentDidMount() {
        const { messageDetails } = this.props;
        // Khi mount, hiển thị tin nhắn mới nhất (100 tin nhắn cuối cùng)
        if (messageDetails && messageDetails.length > 0) {
            const totalMessages = messageDetails.length;
            const startIndex = Math.max(0, totalMessages - MAX_VISIBLE_MESSAGES);
            this.setState({
                visibleStartIndex: startIndex,
                visibleEndIndex: totalMessages
            }, () => {
                // Scroll xuống cuối khi mount
                this.scrollToBottom();
            });
        }
        // Setup scroll listener với delay để đảm bảo DOM đã render
        setTimeout(() => {
            this.setupScrollListener();
        }, 500);
    }

    setupScrollListener() {
        // Tìm scroll container (có thể là .chat-container hoặc element từ react-scrollable-feed)
        const container = document.querySelector('.chat-container') || 
                         document.querySelector('.chat-box') ||
                         this.scrollContainerRef.current?.closest('.chat-container') ||
                         this.scrollContainerRef.current?.closest('.chat-box');
        if (container) {
            container.addEventListener('scroll', this.handleScroll, { passive: true });
        }
    }

    componentDidUpdate(prevProps) {
        const { messageDetails } = this.props;
        const prevLength = prevProps.messageDetails ? prevProps.messageDetails.length : 0;
        const currentLength = messageDetails ? messageDetails.length : 0;
        const { visibleEndIndex } = this.state;

        // Nếu có tin nhắn mới được thêm vào (thường là ở cuối)
        if (currentLength > prevLength) {
            const totalMessages = messageDetails.length;
            
            // Nếu đang xem tin nhắn gần cuối (trong phạm vi 50 tin nhắn cuối), tự động scroll xuống
            const isNearBottom = visibleEndIndex >= totalMessages - 50;
            
            if (isNearBottom) {
                // Hiển thị tin nhắn mới nhất
                const newStartIndex = Math.max(0, totalMessages - MAX_VISIBLE_MESSAGES);
                this.setState({
                    visibleStartIndex: newStartIndex,
                    visibleEndIndex: totalMessages
                }, () => {
                    // Scroll xuống cuối sau khi render
                    setTimeout(() => this.scrollToBottom(), 100);
                });
            } else {
                // Nếu đang xem tin nhắn cũ, chỉ cập nhật endIndex để có thể load thêm
                this.setState({
                    visibleEndIndex: Math.min(totalMessages, visibleEndIndex + (currentLength - prevLength))
                });
            }
        } else if (currentLength < prevLength) {
            // Nếu số lượng tin nhắn giảm (có thể do filter hoặc reset), reset window
            const totalMessages = messageDetails.length;
            const newStartIndex = Math.max(0, totalMessages - MAX_VISIBLE_MESSAGES);
            this.setState({
                visibleStartIndex: newStartIndex,
                visibleEndIndex: totalMessages
            });
        }
    }

    componentWillUnmount() {
        // Cleanup scroll listener
        const container = document.querySelector('.chat-container') || 
                         document.querySelector('.chat-box') ||
                         this.scrollContainerRef.current?.closest('.chat-container') ||
                         this.scrollContainerRef.current?.closest('.chat-box');
        if (container) {
            container.removeEventListener('scroll', this.handleScroll);
        }
    }

    handleScroll(event) {
        const { messageDetails } = this.props;
        const { visibleStartIndex, isLoadingMore } = this.state;
        
        if (isLoadingMore || !messageDetails || messageDetails.length === 0) {
            return;
        }

        const target = event.target;
        const scrollTop = target.scrollTop;
        
        // Nếu scroll gần đầu (để load thêm tin nhắn cũ)
        if (scrollTop < LOAD_MORE_THRESHOLD && visibleStartIndex > 0) {
            this.loadMoreMessages();
        }
    }

    loadMoreMessages() {
        const { messageDetails } = this.props;
        const { visibleStartIndex, isLoadingMore } = this.state;
        
        if (isLoadingMore || visibleStartIndex === 0 || !messageDetails || messageDetails.length === 0) {
            return;
        }

        this.setState({ isLoadingMore: true });

        // Load thêm tin nhắn cũ
        const newStartIndex = Math.max(0, visibleStartIndex - LOAD_MORE_BATCH);
        
        // Lưu scroll position trước khi update
        const container = document.querySelector('.chat-container') || 
                         document.querySelector('.chat-box') ||
                         this.scrollContainerRef.current?.closest('.chat-container') ||
                         this.scrollContainerRef.current?.closest('.chat-box');
        const oldScrollHeight = container ? container.scrollHeight : 0;
        const oldScrollTop = container ? container.scrollTop : 0;

        this.setState({
            visibleStartIndex: newStartIndex,
            isLoadingMore: false
        }, () => {
            // Giữ nguyên scroll position sau khi load (để user không bị jump)
            if (container) {
                requestAnimationFrame(() => {
                    const newScrollHeight = container.scrollHeight;
                    const heightDiff = newScrollHeight - oldScrollHeight;
                    container.scrollTop = oldScrollTop + heightDiff;
                });
            }
        });
    }

    scrollToBottom() {
        // Scroll xuống cuối danh sách
        setTimeout(() => {
            const container = document.querySelector('.chat-container') || 
                             document.querySelector('.chat-box') ||
                             this.scrollContainerRef.current?.closest('.chat-container') ||
                             this.scrollContainerRef.current?.closest('.chat-box');
            if (container) {
                container.scrollTop = container.scrollHeight;
            }
        }, 100);
    }

    getVisibleMessages() {
        const { messageDetails } = this.props;
        const { visibleStartIndex, visibleEndIndex } = this.state;
        
        if (!messageDetails || messageDetails.length === 0) {
            return [];
        }

        // Lấy các tin nhắn trong phạm vi hiển thị
        return messageDetails.slice(visibleStartIndex, visibleEndIndex);
    }

    render() {
        const { messageDetails, user, targetUser } = this.props;
        const { visibleStartIndex, visibleEndIndex } = this.state;
        
        if (!messageDetails || messageDetails.length === 0) {
            return null;
        }

        const visibleMessages = this.getVisibleMessages();
        const hasMoreAbove = visibleStartIndex > 0;
        const totalMessages = messageDetails.length;

        return (
            <div ref={this.scrollContainerRef}>
                {hasMoreAbove && (
                    <div 
                        ref={this.sentinelRef}
                        style={{
                            padding: '10px',
                            textAlign: 'center',
                            color: '#65676b',
                            fontSize: '13px'
                        }}
                    >
                        <i className="fa-solid fa-spinner fa-spin" style={{ marginRight: '8px' }}></i>
                        Đang tải thêm tin nhắn cũ... ({totalMessages - visibleStartIndex} tin nhắn còn lại)
                    </div>
                )}
                {visibleMessages.map((messageDetail) => {
                    // Sử dụng key từ messageDetail thay vì index
                    const key = messageDetail.key || messageDetail.firebaseId || 
                               `${messageDetail.dateTime}-${messageDetail.userId}-${messageDetail.message}`;
                    return (
                        <MessageDetailItem 
                            key={key}
                            messageDetail={messageDetail}
                            user={user}
                            targetUser={targetUser}
                        />
                    );
                })}
            </div>
        );
    }
}